import React from 'react'
import { safeHtml } from '@library/dom'

import {
  AlignItems,
  Body2,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading6,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Spinner,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { FlexPortion } from '@library/api-menu-experience'

import { FlexPortionPicker } from './FlexPortionPicker'

const PromotionMessage = withCitrusTheme(Text, (theme) => ({
  '& .highlight': {
    color: theme.color(Color.Success_900),
    fontWeight: FontWeight.Bold,
  },
}))

const HtmlDescription = withCitrusTheme(Body2, (theme) => ({
  '& p': {
    margin: 0,
  },
  '& p + p': {
    marginTop: theme.spacing(3),
  },
}))

export type FlexPortionEditorState =
  | { status: 'loading' }
  | { status: 'error'; onRetry: () => void }
  | { status: 'success'; portions: FlexPortion[] }

export type FlexPortionEditorProps = {
  recipeCount: number
  numPortions: number
  state: FlexPortionEditorState
  onUpdatePortions: (numPortions: number) => void
  onSelectPortions?: (numPortions: number) => void
  onRemoveRecipe: () => void
  onClose: () => void
}

export const FlexPortionEditor = ({
  recipeCount,
  numPortions,
  state,
  onUpdatePortions,
  onSelectPortions,
  onRemoveRecipe,
  onClose,
}: FlexPortionEditorProps) => {
  const [selectedNumPortions, setSelectedNumPortions] = React.useState(numPortions)
  const portions = state.status === 'success' ? state.portions : []

  React.useEffect(() => {
    setSelectedNumPortions(numPortions)
  }, [numPortions])

  const selectedPortion = portions.find((portion) => portion.numPortions === selectedNumPortions)
  const promotionMessageHTML = selectedPortion?.promotionMessageHTML
  const isUpdateDisabled =
    selectedNumPortions === numPortions || selectedPortion?.isAvailable === false

  const handleSelectPortions = (updatedNumPortions: number) => {
    onSelectPortions?.(updatedNumPortions)
    setSelectedNumPortions(updatedNumPortions)
  }

  return (
    <Box bg={Color.White} data-testid="flex-portion-editor" width="100%">
      <Box
        alignItems={AlignItems.Center}
        borderBottomWidth={0.5}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        paddingH={4}
        paddingV={1}
      >
        <Heading6>{`${recipeCount} ${recipeCount === 1 ? 'recipe' : 'recipes'}`}</Heading6>
        <Button
          aria-label="Close portion editor"
          colorVariant={ButtonColorVariant.Tertiary}
          icon="close"
          onClick={onClose}
          variant={ButtonVariant.Icon}
        />
      </Box>

      {state.status === 'loading' && (
        <Box
          alignItems={AlignItems.Center}
          aria-label="Loading portion options"
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          paddingV={6}
          role="status"
        >
          <Spinner size={6} />
        </Box>
      )}

      {state.status === 'error' && (
        <Box
          alignItems={AlignItems.Center}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={3}
          paddingH={4}
          paddingV={6}
          role="alert"
        >
          <Text>We couldn&apos;t load portion options.</Text>
          <Button colorVariant={ButtonColorVariant.Tertiary} onClick={state.onRetry} type="button">
            Try again
          </Button>
        </Box>
      )}

      {state.status === 'success' && (
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} paddingH={4} paddingV={4}>
          <Box
            aria-describedby="flex-portions-description"
            aria-label="Select number of portions"
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={4}
            paddingBottom={4}
            role="group"
          >
            <Heading6>How many portions?</Heading6>
            <FlexPortionPicker
              onChange={handleSelectPortions}
              portions={portions}
              selectedNumPortions={selectedNumPortions}
            />
          </Box>

          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={3}
            paddingBottom={4}
          >
            {promotionMessageHTML && (
              <Box
                aria-atomic="true"
                aria-live="polite"
                bg={Color.Success_50}
                borderRadius={1.5}
                data-testid="flex-promotion-message"
                paddingH={4}
                paddingV={3}
              >
                <PromotionMessage
                  color={Color.ColdGrey_800}
                  dangerouslySetInnerHTML={safeHtml(promotionMessageHTML)}
                  fontFamily={FontFamily.Book}
                  size={2}
                />
              </Box>
            )}
            <HtmlDescription
              aria-atomic="true"
              aria-live="polite"
              data-testid="flex-description"
              dangerouslySetInnerHTML={safeHtml(selectedPortion?.descriptionHTML ?? '')}
              id="flex-portions-description"
              size={2}
            />
          </Box>

          <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
            <Button
              disabled={isUpdateDisabled}
              onClick={() => onUpdatePortions(selectedNumPortions)}
              width="100%"
            >
              Update portions
            </Button>
            <Space direction="vertical" size={2} />
            <Button
              colorVariant={ButtonColorVariant.Tertiary}
              onClick={onRemoveRecipe}
              width="100%"
            >
              <Icon name="delete" variant={IconVariant.Actionable} />
              <Space direction="horizontal" size={1} />
              Remove recipe
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
